import React from "react";

import { faPlus, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Button from "ui/Button";
import DocsSnippet from "ui/DocsSnippet";
import Panel from "ui/Panel";

export type MatchRule = {
  type: "tag" | "server_name" | "cluster_id" | "role";
  key: string;
  value: string;
};

export const emptyTagRule = (): MatchRule => ({
  type: "tag",
  key: "",
  value: "",
});

export const ruleRequiresKey = (rule: MatchRule): boolean =>
  rule.type === "tag";

const VALUE_LABELS: Record<MatchRule["type"], string> = {
  tag: "Value",
  server_name: "Name",
  cluster_id: "Cluster ID",
  role: "Role",
};

export const toMatchRuleInput = (rules: MatchRule[]) =>
  rules.map((rule) => ({
    matchType: rule.type,
    matchKey: ruleRequiresKey(rule) ? rule.key.trim() : null,
    pattern: rule.value.trim(),
  }));

type Props = {
  name: string;
  description: string;
  matchRules: MatchRule[];
  disabled?: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onMatchRulesChange: (matchRules: MatchRule[]) => void;
};

const ServerGroupForm = ({
  name,
  description,
  matchRules,
  disabled,
  onNameChange,
  onDescriptionChange,
  onMatchRulesChange,
}: Props) => {
  const updateRule = (index: number, updates: Partial<MatchRule>) => {
    onMatchRulesChange(
      matchRules.map((rule, i) =>
        i === index ? { ...rule, ...updates } : rule,
      ),
    );
  };

  const addRule = () => {
    onMatchRulesChange([...matchRules, emptyTagRule()]);
  };

  const removeRule = (index: number) => {
    onMatchRulesChange(matchRules.filter((_, i) => i !== index));
  };

  return (
    <>
      <Panel title="Server Group Details">
        <div className="p-[10px]">
          <div className="form-group">
            <label className="control-label" htmlFor="server_group_name">
              Name <span className="text-[#c00]">*</span>
            </label>
            <input
              id="server_group_name"
              className="form-control max-w-[700px]"
              type="text"
              value={name}
              disabled={disabled}
              onChange={(e) => onNameChange(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="control-label" htmlFor="server_group_description">
              Description
            </label>
            <textarea
              id="server_group_description"
              className="form-control resize-y max-w-[700px]"
              rows={3}
              value={description}
              disabled={disabled}
              onChange={(e) => onDescriptionChange(e.target.value)}
            />
          </div>
        </div>
      </Panel>
      <Panel
        title="Match servers automatically"
        secondaryTitle="All rules must match"
      >
        <div className="p-[10px]">
          {matchRules.map((rule, i) => (
            <div key={i} className="mb-2">
              <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_24px] gap-x-2 pb-1 font-bold">
                <div>Type</div>
                {ruleRequiresKey(rule) ? (
                  <>
                    <div>Key</div>
                    <div>{VALUE_LABELS[rule.type]}</div>
                  </>
                ) : (
                  <div className="col-span-2">
                    {VALUE_LABELS[rule.type]}
                    {rule.type === "cluster_id" && (
                      <>
                        {" "}
                        <DocsSnippet
                          title="Cluster ID"
                          content="Matches the server's cluster identifier, using * as a wildcard."
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_24px] gap-x-2 items-center">
                <select
                  className="form-control"
                  aria-label="Type"
                  value={rule.type}
                  disabled={disabled}
                  onChange={(e) => {
                    const type = e.target.value as MatchRule["type"];
                    updateRule(i, {
                      type,
                      ...(type === "role"
                        ? { value: "primary" }
                        : rule.type === "role"
                        ? { value: "" }
                        : {}),
                    });
                  }}
                >
                  <option value="tag">Tag</option>
                  <option value="server_name">Server name</option>
                  <option value="cluster_id">Cluster ID</option>
                  <option value="role">Role</option>
                </select>
                {ruleRequiresKey(rule) && (
                  <input
                    className="form-control"
                    type="text"
                    required
                    placeholder="e.g. environment"
                    value={rule.key}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { key: e.target.value })}
                  />
                )}
                {rule.type === "role" ? (
                  <select
                    className="form-control col-span-2"
                    aria-label="Role"
                    value={rule.value}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                  >
                    <option value="primary">Primary</option>
                    <option value="replica">Replica</option>
                  </select>
                ) : (
                  <input
                    className={`form-control ${
                      ruleRequiresKey(rule) ? "" : "col-span-2"
                    }`}
                    type="text"
                    required
                    placeholder="use * to match anything, e.g. prod-*"
                    value={rule.value}
                    disabled={disabled}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                  />
                )}
                <Button
                  bare
                  title="Remove"
                  className="text-[#999] hover:text-[#333] cursor-pointer"
                  disabled={disabled}
                  onClick={() => removeRule(i)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            </div>
          ))}
          <Button
            bare
            className="btn btn-primary"
            disabled={disabled}
            onClick={addRule}
          >
            <FontAwesomeIcon icon={faPlus} className="text-[10px] mr-[4px]" />
            Add rule
          </Button>
        </div>
      </Panel>
    </>
  );
};

export default ServerGroupForm;
